/**
 * Linked accounts pane — the per-user surface for connecting accounts. Fully
 * data-driven from the unified `/api/me/connections` list: the workspace's
 * configured + seeded connections (channels, Microsoft, external APIs) with each
 * one's connect-kind, purpose, and status. The connect action dispatches by
 * kind to the matching flow; disconnect is uniform (revokes the link).
 *
 * Channels (Telegram/Slack/Teams/WhatsApp) and Microsoft 365 connect via their
 * proven dialogs (which use the provider-registered callback URLs); OAuth API
 * connections (Odoo, …) use the generic OAuth popup.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

import linkedAccountsEmpty from "@/assets/images/linked-accounts-empty.svg";
import { useOAuthPopup } from "@/hooks/use-oauth-popup";
import {
  meConnectionsService,
  type ConnectionProfileInput,
  type LinkedAccount,
  type MeConnection,
} from "@/services/me-connections-service";
import type { LinkResponse } from "@/services/external-identity-service";
import { ConnectionRow } from "./connection-row";
import { ConnectAccountProfileDialog } from "./connect-account-profile-dialog";
import { ConnectChannelDialog } from "./connect-channel-dialog";
import { ConnectTelegramDialog } from "./connect-telegram-dialog";
import { ConnectWhatsappDialog } from "./connect-whatsapp-dialog";
import { ConnectStaticCredentialDialog } from "./connect-static-credential-dialog";
import { EditLinkedAccountDialog } from "./edit-linked-account-dialog";
import { MergePriorHistoryDialog } from "./merge-prior-history-dialog";

/**
 * Connect-kinds still handled by the bespoke channel dialog — now empty. Every
 * kind goes through the unified endpoint: OAuth-popup kinds (oauth2/slack_oauth/
 * microsoft_oauth/teams_oidc) via the popup hook, Telegram via its widget
 * dialog, WhatsApp via its OTP dialog. Kept for any future generic kind.
 */
const CHANNEL_KINDS = new Set<string>([]);

export function ConnectedAccountsPane() {
  const [connections, setConnections] = useState<MeConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  // Dispatch targets for the per-kind connect flows.
  const [channelDialog, setChannelDialog] = useState<string | null>(null);
  const [telegramConn, setTelegramConn] = useState<MeConnection | null>(null);
  const [whatsappOpen, setWhatsappOpen] = useState(false);
  const [staticConn, setStaticConn] = useState<MeConnection | null>(null);
  const [profileConn, setProfileConn] = useState<MeConnection | null>(null);
  const [mergePrompt, setMergePrompt] = useState<LinkResponse | null>(null);
  const [editingAccount, setEditingAccount] = useState<{
    conn: MeConnection;
    account: LinkedAccount;
  } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setConnections(await meConnectionsService.list());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load connections.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const connectOAuth = useOAuthPopup({ onDone: load, setBusy });

  const handleConnect = useCallback(
    async (conn: MeConnection) => {
      if (conn.connectKind === "telegram_widget") {
        setTelegramConn(conn);
        return;
      }
      if (conn.connectKind === "phone_otp") {
        setWhatsappOpen(true);
        return;
      }
      if (
        conn.connectKind === "bearer" ||
        conn.connectKind === "api_key" ||
        conn.connectKind === "basic_auth"
      ) {
        // Member supplies the secret directly — no OAuth round-trip.
        setStaticConn(conn);
        return;
      }
      if (CHANNEL_KINDS.has(conn.connectKind)) {
        setChannelDialog(conn.provider);
        return;
      }
      setProfileConn(conn);
    },
    [],
  );

  const continueOAuthConnect = useCallback(
    async (profile: ConnectionProfileInput) => {
      if (!profileConn) return;
      const conn = profileConn;
      setProfileConn(null);
      await connectOAuth(conn, profile);
    },
    [connectOAuth, profileConn],
  );

  const handleDisconnectAccount = useCallback(
    async (conn: MeConnection, account: LinkedAccount) => {
      setBusy(account.id);
      try {
        await meConnectionsService.disconnectAccount(conn.provider, account.id);
        toast.success(`${account.accountLabel || account.displayName || conn.displayName} disconnected`);
        await load();
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Could not disconnect.");
      } finally {
        setBusy(null);
      }
    },
    [load],
  );

  const handleLinked = useCallback(
    async (response: LinkResponse) => {
      setChannelDialog(null);
      await load();
      if (response.orphan) setMergePrompt(response);
      else toast.success("Account linked");
    },
    [load],
  );

  // Channels first, then API connections — a stable, readable order.
  const ordered = useMemo(() => {
    const rank = (c: MeConnection) => (c.purpose === "identity" ? 0 : 1);
    return [...connections].sort((a, b) => rank(a) - rank(b));
  }, [connections]);

  return (
    <div className="space-y-6 p-6">
      <div className="space-y-1">
        <h2 className="text-xl font-semibold tracking-tight">Linked Accounts</h2>
        <p className="truncate text-sm text-muted-foreground">
          Link your personal accounts so agents recognize you across channels and
          can call authenticated external APIs on your behalf.
        </p>
      </div>

      {error ? (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
          {error}
        </div>
      ) : null}

      {loading && connections.length === 0 ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="size-3 animate-spin" /> Loading…
        </div>
      ) : ordered.length === 0 ? (
        <EmptyConnectionsState />
      ) : (
        <div className="space-y-3">
          {ordered.map((conn) => (
            <ConnectionRow
              key={conn.provider}
              conn={conn}
              busy={
                busy === conn.provider ||
                conn.linkedAccounts.some((account) => account.id === busy)
              }
              onConnect={handleConnect}
              onEditAccount={(connection, account) =>
                setEditingAccount({ conn: connection, account })
              }
              onDisconnectAccount={handleDisconnectAccount}
            />
          ))}
        </div>
      )}

      {channelDialog ? (
        <ConnectChannelDialog
          channel={channelDialog}
          onClose={() => setChannelDialog(null)}
          onLinked={handleLinked}
        />
      ) : null}

      {telegramConn ? (
        <ConnectTelegramDialog
          botUsername={telegramConn.config?.botUsername}
          onClose={() => setTelegramConn(null)}
          onConnected={async () => {
            setTelegramConn(null);
            await load();
          }}
        />
      ) : null}

      {whatsappOpen ? (
        <ConnectWhatsappDialog
          onClose={() => setWhatsappOpen(false)}
          onConnected={async () => {
            setWhatsappOpen(false);
            await load();
          }}
        />
      ) : null}

      {staticConn ? (
        <ConnectStaticCredentialDialog
          conn={staticConn}
          onClose={() => setStaticConn(null)}
          onConnected={async () => {
            setStaticConn(null);
            await load();
          }}
        />
      ) : null}

      {profileConn ? (
        <ConnectAccountProfileDialog
          conn={profileConn}
          onClose={() => setProfileConn(null)}
          onContinue={continueOAuthConnect}
        />
      ) : null}

      {editingAccount ? (
        <EditLinkedAccountDialog
          conn={editingAccount.conn}
          account={editingAccount.account}
          onClose={() => setEditingAccount(null)}
          onSaved={async () => {
            setEditingAccount(null);
            await load();
          }}
        />
      ) : null}

      {mergePrompt ? (
        <MergePriorHistoryDialog
          identityId={mergePrompt.identity.id}
          orphan={mergePrompt.orphan!}
          onDone={() => setMergePrompt(null)}
        />
      ) : null}
    </div>
  );
}

function EmptyConnectionsState() {
  return (
    <div className="rounded-lg border border-border bg-muted/10 px-6 py-5">
      <div className="flex items-center gap-6">
        <img
          src={linkedAccountsEmpty}
          alt=""
          className="h-28 w-40 shrink-0 object-contain grayscale"
        />
        <div className="min-w-0">
          <p className="text-sm font-medium text-foreground">No connections available</p>
          <p className="mt-1 max-w-xl text-sm leading-6 text-muted-foreground">
            Add API Connections in workspace settings, then members can link the
            accounts agents should use on their behalf.
          </p>
        </div>
      </div>
    </div>
  );
}
